import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Building2, UserSearch } from 'lucide-react';
import { AdvancedFilterPanel, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { SelectedFilterChip } from '@/components/common/SelectedFilterChip';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useKeepWindowScrollOnReturn } from '@/hooks/useKeepWindowScrollOnReturn';
import { parseNumberFilter } from '@/utils/filters';
import { getUniqueBy } from '@/utils/uniqueBy';
import { teacherListInfiniteQueryOptions } from '../api/teacherSubmissions.api';
import { getTeacherSubmissionColumns } from '../components/TeacherSubmissionColumns';
import { TeacherSubmissionListHeader } from '../components/TeacherSubmissionListHeader';
import { TeacherSubmissionSummaryCards } from '../components/TeacherSubmissionSummaryCards';
import { TEACHER_LIST_PAGE_SIZE } from '../constants/teacherSubmission.constants';
import { useTeacherSubmissionFilterFields } from '../hooks/useTeacherSubmissionFilterFields';
import { useTeacherSubmissionFilters } from '../hooks/useTeacherSubmissionFilters';
import { useLastViewedTeacherStore } from '../store/lastViewedTeacher.store';
import type { TeacherListItem } from '../types/teacherSubmission.types';

const INSTITUTE_NAME_FALLBACK = 'Selected institute';

export function TeacherSubmissionListPage() {
  const navigate = useNavigate();
  const {
    searchText,
    filterValues,
    isFilterOpen,
    setIsFilterOpen,
    activeFilterCount,
    hasPendingChanges,
    listParams,
    summaryParams,
    countsBySubmitterParams,
    handleSearchApply,
    handleFilterChange,
    applyFilterValues,
  } = useTeacherSubmissionFilters();

  const { rows, isLoading, loadError, refetch, loadMoreProps } = useInfiniteTableQuery(
    teacherListInfiniteQueryOptions(listParams),
  );
  const teachers = useMemo(() => getUniqueBy(rows, (teacher) => teacher.teacherId), [rows]);

  const columns = useMemo(
    () => getTeacherSubmissionColumns({ searchText: listParams.search }),
    [listParams.search],
  );
  const lastViewedTeacherId = useLastViewedTeacherStore((state) => state.lastViewedTeacherId);
  const selectedInstituteName =
    teachers.find((teacher) => teacher.instituteId === summaryParams.instituteId)?.instituteName ??
    INSTITUTE_NAME_FALLBACK;

  const { fields, hasOptionsError } = useTeacherSubmissionFilterFields({
    isEnabled: isFilterOpen,
    selectedInstituteId: summaryParams.instituteId,
    selectedInstituteName,
    districtId: parseNumberFilter(filterValues.districtId),
    amUserId: parseNumberFilter(filterValues.amUserId),
    countsBySubmitterParams,
  });

  useKeepWindowScrollOnReturn(lastViewedTeacherId !== null);

  function handleRowClick(teacher: TeacherListItem) {
    void navigate({
      to: '/settings/teacher-submissions/$teacherId',
      params: { teacherId: teacher.teacherId },
    });
  }

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <TeacherSubmissionListHeader
        appliedSearchText={searchText}
        onSearchApply={handleSearchApply}
        isFilterOpen={isFilterOpen}
        onFilterToggle={() => setIsFilterOpen((wasOpen) => !wasOpen)}
        activeFilterCount={activeFilterCount}
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <div className="flex flex-col gap-2">
          <AdvancedFilterPanel
            fields={fields}
            values={filterValues}
            onChange={handleFilterChange}
            onApply={applyFilterValues}
            hasPendingChanges={hasPendingChanges}
          />
          {hasOptionsError && (
            <p className="text-xs text-destructive">
              Some filter options could not be loaded. Refresh the page to try again.
            </p>
          )}
        </div>
      </FilterPanelCollapse>

      {summaryParams.instituteId !== undefined && (
        <SelectedFilterChip
          icon={Building2}
          label="Institute"
          value={selectedInstituteName}
          clearLabel="Show teachers of every institute"
          onClear={() => applyFilterValues({ ...filterValues, instituteId: '' })}
        />
      )}

      <TeacherSubmissionSummaryCards params={summaryParams} />

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load teacher submissions"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={teachers}
          columns={columns}
          isLoading={isLoading}
          stickyColumnId="name"
          className="lg:flex-1"
          emptyIcon={UserSearch}
          skeletonRowCount={TEACHER_LIST_PAGE_SIZE}
          {...loadMoreProps}
          isRowHighlighted={(teacher) => teacher.teacherId === lastViewedTeacherId}
          onRowClick={handleRowClick}
        />
      )}
    </div>
  );
}
